import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Save,
  Info,
  ChevronDown,
  ChevronLeft,
  ArrowLeft,
  ArrowRight,
  Check,
  User,
} from "lucide-react";

import {
  fetchLeaveTypes,
  createLeaveType,
  updateLeaveType,
  deleteLeaveType,
  fetchLeavePolicies,
  createLeavePolicy,
  updateLeavePolicy,
  fetchLeaveSettings,
} from "../../Moduels/leave/leaveApi";
import { useLeaveOrg } from "../pages/Leave/useLeaveOrg";
import { fetchIamRoles, fetchIamUsers } from "../../Moduels/IAM/iamApi";
import { useToast } from "../../Moduels/Context/ToastContext";
import LeaveCycleTab from "./LeaveCycleTab";
import { useSetupProgress } from "../../Moduels/Context/SetupProgressContext";
import { markLeaveSetupConfigured } from "../../services/markLeaveSetupConfigured";
import { navigateToSettingsHome } from "./settingsExit";
import SuccessScreen from "../common/SuccessScreen";
import "./AttendanceLeaveSetup.css";
import "./LeaveSetup.css";

// ─── helpers ─────────────────────────────────────────────────────────────────

function fmtErr(e, fallback = "Something went wrong") {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((i) => i?.msg || String(i)).join("; ");
  return fallback;
}

/** Annual limit may live on leave-type applicability OR policy rules (seeded defaults). */
function resolveAnnualLimitDays(leaveType, policy) {
  const fromType = leaveType?.applicability_json?.annual_limit_days;
  if (fromType != null && fromType !== "") return Number(fromType);
  const rules = policy?.rules_json || {};
  const fromPolicy = rules.yearly_limit ?? rules.annual_entitlement_days;
  if (fromPolicy != null && fromPolicy !== "") return Number(fromPolicy);
  return null;
}

function enrichLeaveTypesWithPolicies(types, policies) {
  const byType = {};
  for (const p of policies || []) {
    const id = String(p.leave_type_id);
    if (!byType[id] || (p.is_active && !byType[id].is_active)) {
      byType[id] = p;
    }
  }
  return (types || []).map((t) => {
    const policy = byType[String(t.leave_type_id)];
    const annual = resolveAnnualLimitDays(t, policy);
    return {
      ...t,
      _policy: policy || null,
      annual_limit_days: annual,
      applicability_json: {
        ...(t.applicability_json || {}),
        ...(annual != null ? { annual_limit_days: annual } : {}),
      },
    };
  });
}

// ─── constants ────────────────────────────────────────────────────────────────

const TABS = [
  { key: "leave-cycle", label: "Leave Cycle" },
  { key: "leave-types", label: "Leave Types" },
  { key: "leave-policies", label: "Accrual Policy" },
  { key: "approval-workflow", label: "Workflow & Approval" },
];

const ACCRUAL_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "as_earned", label: "As Earned" },
];

const ACCRUAL_FREQUENCY_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "half_yearly", label: "Half Yearly" },
  { value: "annual", label: "Annual" },
];

// ─── Applicability options ────────────────────────────────────────────────────

const EMPLOYMENT_TYPES = [
  { value: "Permanent", label: "Regular" },
  { value: "Contract", label: "Contractor" },
  { value: "Intern", label: "Intern" },
];

// ─── Leave Type Form ──────────────────────────────────────────────────────────

function formatApplicableLabel(applicable) {
  if (!applicable || applicable === "all_employees") return "All Employees";
  if (Array.isArray(applicable)) {
    return applicable
      .map((v) => EMPLOYMENT_TYPES.find((t) => t.value === v)?.label || v)
      .join(", ");
  }
  return "All Employees";
}

const EMPTY_LEAVE_TYPE_FORM = {
  code: "",
  name: "",
  description: "",
  is_paid: true,
  annual_limit: "",
  allow_half_day: true,
  combine_with_other: true,
  max_consecutive_days: 0,
  applicability_mode: "all",
  selected_types: [],
};

function leaveTypeToForm(leaveType) {
  const annual =
    leaveType.annual_limit_days ??
    leaveType.applicability_json?.annual_limit_days ??
    null;
  const appJson = leaveType.applicability_json || {};
  const raw = appJson.applicable_to;
  const isSelected = Array.isArray(raw) && raw.length > 0;
  return {
    code: leaveType.code || "",
    name: leaveType.name || "",
    description: leaveType.description || "",
    is_paid: leaveType.is_paid !== false,
    annual_limit: annual != null && annual !== "" ? String(annual) : "",
    allow_half_day: appJson.allow_half_day !== false,
    combine_with_other: appJson.combine_with_other !== false,
    max_consecutive_days: appJson.max_consecutive_days ?? 0,
    applicability_mode: isSelected ? "selected" : "all",
    selected_types: isSelected ? raw : [],
  };
}

function validateLeaveTypeForm(form) {
  const errors = {};
  if (!form.code.trim()) errors.code = "Leave Code is required.";
  if (!form.name.trim()) errors.name = "Leave Name is required.";
  const maxDays = Number(form.max_consecutive_days);
  if (form.max_consecutive_days === "" || isNaN(maxDays) || maxDays < 0 || !Number.isInteger(maxDays)) {
    errors.max_consecutive_days = "Enter a whole number (0 or more).";
  }
  if (form.applicability_mode === "selected" && form.selected_types.length === 0) {
    errors.selected_types = "Select at least one employment type.";
  }
  return errors;
}

function YesNoChoice({ name, value, onChange }) {
  return (
    <div className="lss-radio-group" role="radiogroup">
      <label className={`lss-radio-option ${value ? "lss-radio-option--active" : ""}`}>
        <input type="radio" name={name} checked={value} onChange={() => onChange(true)} />
        Yes
      </label>
      <label className={`lss-radio-option ${!value ? "lss-radio-option--active" : ""}`}>
        <input type="radio" name={name} checked={!value} onChange={() => onChange(false)} />
        No
      </label>
    </div>
  );
}

/** Full-page Add / Edit Leave Type form (replaces the former modal). */
function LeaveTypeFormPage({ orgId, leaveTypeId, onClose, onSaved }) {
  const isEdit = Boolean(leaveTypeId);
  // Set after the API confirms the save: { id } of the saved leave type.
  const [savedResult, setSavedResult] = useState(null);
  const [form, setForm] = useState(EMPTY_LEAVE_TYPE_FORM);
  const [leaveType, setLeaveType] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const savingRef = useRef(false);
  const errorRef = useRef(null);

  useEffect(() => {
    if (!isEdit || !orgId) return undefined;
    let alive = true;
    setLoading(true);
    setLoadError("");
    Promise.all([fetchLeaveTypes(orgId), fetchLeavePolicies(orgId).catch(() => [])])
      .then(([types, policies]) => {
        if (!alive) return;
        const found = enrichLeaveTypesWithPolicies(types, policies).find(
          (t) => String(t.leave_type_id) === String(leaveTypeId)
        );
        if (!found) {
          setLoadError("This leave type could not be found. It may have been deleted.");
          return;
        }
        setLeaveType(found);
        setForm(leaveTypeToForm(found));
      })
      .catch((e) => { if (alive) setLoadError(fmtErr(e, "Failed to load leave type")); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [isEdit, orgId, leaveTypeId]);

  const scrollToError = () =>
    requestAnimationFrame(() => errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }));

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((fe) => (fe[k] ? { ...fe, [k]: undefined } : fe));
  };

  const toggleSelectedType = (val) => {
    setForm((f) => {
      const cur = f.selected_types;
      const next = cur.includes(val) ? cur.filter((v) => v !== val) : [...cur, val];
      return { ...f, selected_types: next };
    });
    setFieldErrors((fe) => (fe.selected_types ? { ...fe, selected_types: undefined } : fe));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingRef.current) return;
    const errs = validateLeaveTypeForm(form);
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      setError("Please fix the highlighted fields.");
      scrollToError();
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError("");
    setFieldErrors({});
    try {
      const applicableTo =
        form.applicability_mode === "selected" ? form.selected_types : "all_employees";
      const payload = {
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        description: form.description.trim() || null,
        is_paid: form.is_paid,
        applicability_json: {
          allow_half_day: form.allow_half_day,
          combine_with_other: form.combine_with_other,
          max_consecutive_days: Number(form.max_consecutive_days),
          applicable_to: applicableTo,
        },
      };
      let saved;
      if (isEdit) {
        const { code, ...updatePayload } = payload;
        saved = await updateLeaveType(orgId, leaveType.leave_type_id, updatePayload);
      } else {
        saved = await createLeaveType(orgId, payload);
      }
      setSavedResult({ id: saved?.leave_type_id ?? leaveType?.leave_type_id ?? null });
    } catch (err) {
      setError(fmtErr(err, "Failed to save leave type"));
      scrollToError();
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="lss-form-page">
      <button type="button" className="lss-back-link" onClick={onClose}>
        <ArrowLeft size={15} /> Back to Leave Types
      </button>

      {savedResult ? (
        <SuccessScreen
          variant="panel"
          title={isEdit ? "Leave type updated successfully!" : "Leave type created successfully!"}
          message="Redirecting to leave types…"
          onComplete={() => onSaved?.(savedResult.id)}
        />
      ) : loading ? (
        <div className="lss-form-card lss-form-state">
          <Loader2 size={18} className="als-spin" /> Loading leave type…
        </div>
      ) : loadError ? (
        <div className="lss-form-card lss-form-state lss-form-state--error">
          <div className="lss-error-bar">{loadError}</div>
          <button type="button" className="lss-btn-secondary" onClick={onClose}>
            <ArrowLeft size={14} /> Back to Leave Types
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="lss-form-card" noValidate>
          {error && (
            <div className="lss-form-error" ref={errorRef} role="alert">
              <div className="lss-error-bar">{error}</div>
            </div>
          )}

          {/* ── Section 1: Basic Information ── */}
          <section className="lss-form-section">
            <h2 className="lss-form-section-title">Basic Information</h2>
            <div className="lss-form-grid lss-form-grid--basic">
              <div className="lss-form-row">
                <label className="lss-label" htmlFor="lt-code">
                  Leave Code <span className="lss-required">*</span>
                </label>
                <input
                  id="lt-code"
                  className={`lss-input${fieldErrors.code ? " lss-input--error" : ""}`}
                  placeholder="e.g. CL"
                  value={form.code}
                  onChange={(e) => setField("code", e.target.value)}
                  disabled={isEdit}
                  maxLength={10}
                  aria-invalid={Boolean(fieldErrors.code)}
                  title={isEdit ? "The leave code cannot be changed after creation." : "Short code, up to 10 characters."}
                />
                {fieldErrors.code && <span className="lss-field-error">{fieldErrors.code}</span>}
              </div>
              <div className="lss-form-row">
                <label className="lss-label" htmlFor="lt-name">
                  Leave Name <span className="lss-required">*</span>
                </label>
                <input
                  id="lt-name"
                  className={`lss-input${fieldErrors.name ? " lss-input--error" : ""}`}
                  placeholder="e.g. Casual Leave"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  maxLength={120}
                  aria-invalid={Boolean(fieldErrors.name)}
                />
                {fieldErrors.name && <span className="lss-field-error">{fieldErrors.name}</span>}
              </div>
              <div className="lss-form-row lss-form-row--paid">
                <span className="lss-label">Paid Leave</span>
                <div className="lss-toggle-wrap lss-toggle-wrap--field">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={form.is_paid}
                    aria-label="Paid Leave"
                    className={`lss-toggle ${form.is_paid ? "lss-toggle--on" : ""}`}
                    onClick={() => setField("is_paid", !form.is_paid)}
                  >
                    <span className="lss-toggle-knob" />
                  </button>
                  <span className="lss-toggle-label">{form.is_paid ? "Yes" : "No"}</span>
                </div>
              </div>
              <div className="lss-form-row lss-form-row--desc">
                <label className="lss-label" htmlFor="lt-desc">Description</label>
                <input
                  id="lt-desc"
                  className="lss-input"
                  placeholder="Short description of this leave type"
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                />
              </div>
              {/* Annual Limit hidden — the annual entitlement is set in the Accrual Policy step. */}
            </div>
          </section>

          {/* ── Section 2: Leave Behaviour ── */}
          <section className="lss-form-section">
            <h2 className="lss-form-section-title">Leave Behaviour</h2>
            <div className="lss-behaviour-grid">
              <div className="lss-behaviour-item">
                <span className="lss-label">Can employee apply for half day?</span>
                <YesNoChoice
                  name="allow_half_day"
                  value={form.allow_half_day}
                  onChange={(v) => setField("allow_half_day", v)}
                />
              </div>
              <div className="lss-behaviour-item">
                <span className="lss-label">Can this leave be combined with other leave types?</span>
                <YesNoChoice
                  name="combine_with_other"
                  value={form.combine_with_other}
                  onChange={(v) => setField("combine_with_other", v)}
                />
              </div>
              <div className="lss-behaviour-item">
                <label className="lss-label" htmlFor="lt-maxdays">
                  Maximum consecutive days allowed <span className="lss-label-hint">(0 for no limit)</span>
                </label>
                <div className="lss-input-with-unit">
                  <input
                    id="lt-maxdays"
                    className={`lss-input${fieldErrors.max_consecutive_days ? " lss-input--error" : ""}`}
                    type="number"
                    min={0}
                    step={1}
                    value={form.max_consecutive_days}
                    onChange={(e) => setField("max_consecutive_days", e.target.value)}
                    aria-invalid={Boolean(fieldErrors.max_consecutive_days)}
                  />
                  <span className="lss-input-unit">days</span>
                </div>
                {fieldErrors.max_consecutive_days && (
                  <span className="lss-field-error">{fieldErrors.max_consecutive_days}</span>
                )}
              </div>
            </div>
          </section>

          {/* ── Section 3: Applicability ── */}
          <section className="lss-form-section">
            <div className="lss-form-section-head lss-form-section-head--compact">
              <h2 className="lss-form-section-title">Applicability</h2>
              <p className="lss-form-section-sub">Choose which employees can avail this leave.</p>
            </div>
            <div className="lss-applicability">
              <div className="lss-form-row">
                <span className="lss-label">
                  Applicable To <span className="lss-required">*</span>
                </span>
                <div className="lss-radio-group lss-radio-group--wrap" role="radiogroup">
                  <label className={`lss-radio-option ${form.applicability_mode === "all" ? "lss-radio-option--active" : ""}`}>
                    <input
                      type="radio"
                      name="applicability_mode"
                      checked={form.applicability_mode === "all"}
                      onChange={() => setField("applicability_mode", "all")}
                    />
                    All Employees
                  </label>
                  <label className={`lss-radio-option ${form.applicability_mode === "selected" ? "lss-radio-option--active" : ""}`}>
                    <input
                      type="radio"
                      name="applicability_mode"
                      checked={form.applicability_mode === "selected"}
                      onChange={() => setField("applicability_mode", "selected")}
                    />
                    Selected Types
                  </label>
                </div>
                {form.applicability_mode !== "selected" && (
                  <span className="lss-helper-text">This leave type will be available to all employees.</span>
                )}
              </div>

              {form.applicability_mode === "selected" && (
                <div className="lss-form-row">
                  <span className="lss-label">Employment Types</span>
                  <div className="lss-checkbox-group">
                    {EMPLOYMENT_TYPES.map((t) => (
                      <label key={t.value} className={`lss-checkbox-option ${form.selected_types.includes(t.value) ? "lss-checkbox-option--active" : ""}`}>
                        <input
                          type="checkbox"
                          checked={form.selected_types.includes(t.value)}
                          onChange={() => toggleSelectedType(t.value)}
                        />
                        {t.label}
                      </label>
                    ))}
                  </div>
                  {fieldErrors.selected_types && (
                    <span className="lss-field-error">{fieldErrors.selected_types}</span>
                  )}
                </div>
              )}
            </div>
          </section>

          <div className="lss-form-actions">
            <button type="button" className="lss-btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="lss-btn-primary" disabled={saving}>
              {saving ? <Loader2 size={14} className="als-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : isEdit ? "Save Changes" : "Save Leave Type"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Accrual Policy Form (full page) ──────────────────────────────────────────

const ACCRUAL_PERIODS = { monthly: 12, quarterly: 4, half_yearly: 2, annual: 1 };
const ACCRUAL_PERIOD_NOUN = { monthly: "month", quarterly: "quarter", half_yearly: "half year", annual: "year" };
const ACCRUAL_STEP_DAYS = 0.5; // each credit must be 0.5, 1, 1.5, 2 … days

/** Timing and accrual day are one choice: beginning → first day, end → last day of the period. */
const ACCRUAL_TIMING_CHOICES = [
  { value: "beginning", day: "first", label: "Beginning of period", hint: "Credited on the first day of every" },
  { value: "end", day: "last", label: "End of period", hint: "Credited on the last day of every" },
];

const CF_EXPIRY_OPTIONS = [
  { value: "year_end", label: "Year end" },
  { value: "month_end", label: "Month end" },
  { value: "end_of_next_cycle", label: "End of next cycle" },
  { value: "3_months", label: "After 3 months" },
  { value: "6_months", label: "After 6 months" },
  { value: "never", label: "Never expire" },
];

/**
 * Per-period credit for an annual entitlement. Valid only when it is a whole multiple of
 * 0.5 days (0.5, 1, 1.5, 2 …); otherwise ``valid`` is false and nearby valid entitlements
 * are suggested.
 */
function accrualAmountFor(entitlement, frequency) {
  const days = Number(entitlement);
  const periods = ACCRUAL_PERIODS[frequency] || 12;
  if (entitlement === "" || !Number.isFinite(days) || days <= 0) return { amount: null, valid: false, periods };
  const amount = days / periods;
  const steps = amount / ACCRUAL_STEP_DAYS;
  const valid = amount >= ACCRUAL_STEP_DAYS && Math.abs(steps - Math.round(steps)) < 1e-9;
  const unit = ACCRUAL_STEP_DAYS * periods; // entitlement must be a multiple of this
  const lower = Math.max(unit, Math.floor(days / unit) * unit);
  const higher = Math.ceil(days / unit) * unit;
  return { amount, valid, periods, unit, suggestions: [...new Set([lower, higher])].filter((v) => v !== days) };
}

/** Whole-number inputs: keep digits only and block keys that type decimals, signs or exponents. */
// A pasted "12.5" keeps its whole part (12), not "125".
const digitsOnly = (value) => String(value).split(/[.,]/)[0].replace(/\D+/g, "");
const blockNonIntegerKeys = (e) => {
  if ([".", ",", "e", "E", "+", "-"].includes(e.key)) e.preventDefault();
};

const fmtDays = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `${Number.isInteger(v) ? v : v.toFixed(2).replace(/0+$/, "").replace(/\.$/, "")} ${v === 1 ? "day" : "days"}`;
};

function policyToAccrualForm(policy) {
  const r = policy.rules_json || {};
  const acc = r.accrual || {};
  const cf = r.carry_forward || {};
  const freq = ACCRUAL_PERIODS[acc.frequency] ? acc.frequency : ACCRUAL_PERIODS[r.accrual_method] ? r.accrual_method : r.accrual_method === "yearly" ? "annual" : "monthly";
  const rawAmount = acc.amount ?? acc.rate_per_month;
  const savedEntitlement = r.annual_entitlement_days ?? r.yearly_limit;
  const entitlement =
    savedEntitlement != null && savedEntitlement !== ""
      ? savedEntitlement
      : rawAmount != null
        ? Number(rawAmount) * (ACCRUAL_PERIODS[freq] || 12)
        : "";
  // Saved max_days 0 meant "unlimited" in the old form; a positive cap is "maximum days".
  const savedMax = Number(cf.max_days ?? r.cf_max_days ?? 0);
  return {
    annual_entitlement: entitlement === "" ? "" : String(Number(entitlement)),
    accrual_frequency: freq,
    accrual_timing: (acc.timing || r.accrual_timing) === "end" ? "end" : "beginning",
    joining_cutoff_day: acc.joining_cutoff_day ?? r.joining_cutoff_day ?? 15,
    // Explicit choice when saved by this page; older policies set to expire keep "expire"; else the user must choose.
    monthly_unused:
      r.monthly_unused_treatment === "expire" || r.monthly_unused_treatment === "carry_forward"
        ? r.monthly_unused_treatment
        : r.advanced_accrual_cf && cf.decision === "expire"
          ? "expire"
          : "",
    cf_enabled: Boolean(r.carry_forward_allowed ?? cf.enabled),
    cf_limit: savedMax > 0 ? "max_days" : "unlimited",
    cf_max_days: savedMax > 0 ? String(savedMax) : "",
    cf_expiry: cf.expiry || r.cf_expiry || "year_end",
    negative_balance: Boolean(r.negative_balance ?? cf.negative_balance),
    max_negative_days: String(r.max_negative_days ?? cf.max_negative_days ?? "0"),
    effective_from: policy.effective_from || new Date().toISOString().slice(0, 10),
  };
}

function validateAccrualForm(form) {
  const errors = {};
  const ent = Number(form.annual_entitlement);
  if (form.annual_entitlement === "" || !Number.isFinite(ent) || ent <= 0) {
    errors.annual_entitlement = "Enter the annual entitlement in days (more than 0).";
  } else if (!Number.isInteger(ent)) {
    errors.annual_entitlement = "Annual entitlement must be a whole number of days.";
  } else if (ent > 366) {
    errors.annual_entitlement = "Annual entitlement cannot exceed 366 days.";
  } else {
    const calc = accrualAmountFor(form.annual_entitlement, form.accrual_frequency);
    if (!calc.valid) {
      const noun = ACCRUAL_PERIOD_NOUN[form.accrual_frequency] || "month";
      const hint = calc.suggestions?.length ? ` Try ${calc.suggestions.map((v) => fmtDays(v)).join(" or ")}.` : "";
      errors.annual_entitlement =
        `Accrual amount would be ${fmtDays(Number(calc.amount.toFixed(4)))} per ${noun}; only 0.5, 1, 1.5, 2 … days are allowed. ` +
        `Change the annual entitlement to a multiple of ${fmtDays(calc.unit)}.${hint}`;
    }
  }
  if (!form.monthly_unused) errors.monthly_unused = "Select a treatment.";
  const cutoff = Number(form.joining_cutoff_day);
  if (!Number.isInteger(cutoff) || cutoff < 1 || cutoff > 28) {
    errors.joining_cutoff_day = "Enter a day between 1 and 28.";
  }
  if (form.monthly_unused === "carry_forward" && form.cf_limit === "max_days") {
    const v = Number(form.cf_max_days);
    if (form.cf_max_days === "" || !Number.isFinite(v) || v <= 0) errors.cf_max_days = "Enter the maximum days (more than 0).";
  }
  return errors;
}

function AccrualPolicyFormPage({ orgId, policyId, onClose, onSaved }) {
  const [policy, setPolicy] = useState(null);
  const [saved, setSaved] = useState(false);
  // Read by the Leave Type Information section (commented out below).
  // eslint-disable-next-line no-unused-vars
  const [leaveType, setLeaveType] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const savingRef = useRef(false);
  const errorRef = useRef(null);

  useEffect(() => {
    if (!orgId || !policyId) return undefined;
    let alive = true;
    setLoading(true);
    setLoadError("");
    Promise.all([fetchLeavePolicies(orgId), fetchLeaveTypes(orgId)])
      .then(([pols, types]) => {
        if (!alive) return;
        const found = (Array.isArray(pols) ? pols : []).find((p) => String(p.policy_id) === String(policyId));
        if (!found) {
          setLoadError("This accrual policy could not be found. It may have been removed.");
          return;
        }
        setPolicy(found);
        setLeaveType((types || []).find((t) => String(t.leave_type_id) === String(found.leave_type_id)) || null);
        setForm(policyToAccrualForm(found));
      })
      .catch((e) => { if (alive) setLoadError(fmtErr(e, "Failed to load accrual policy")); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orgId, policyId]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((fe) => {
      if (!fe[k] && !(k === "accrual_frequency" && fe.annual_entitlement)) return fe;
      // Frequency changes the allowed entitlements, so it also clears that message.
      return { ...fe, [k]: undefined, ...(k === "accrual_frequency" ? { annual_entitlement: undefined } : {}) };
    });
  };

  const scrollToError = () =>
    requestAnimationFrame(() => errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }));

  const calc = form ? accrualAmountFor(form.annual_entitlement, form.accrual_frequency) : { amount: null, valid: false, periods: 12 };
  const periodNoun = form ? ACCRUAL_PERIOD_NOUN[form.accrual_frequency] || "month" : "month";
  const timing = form ? ACCRUAL_TIMING_CHOICES.find((t) => t.value === form.accrual_timing) || ACCRUAL_TIMING_CHOICES[0] : null;
  const monthlyExpire = form?.monthly_unused === "expire";
  const monthlyCarry = form?.monthly_unused === "carry_forward";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingRef.current || !form) return;
    const errs = validateAccrualForm(form);
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      setError("Please fix the highlighted fields.");
      scrollToError();
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError("");
    try {
      const annualDays = Number(form.annual_entitlement);
      const rules = policy.rules_json || {};
      // Unused leave expiring every period: the leave engine's period-close expiry
      // (advanced_accrual_cf + decision "expire"). Nothing is left to carry at year end.
      // Carry forward follows the Treatment choice (its own toggle is hidden).
      const cfEnabled = monthlyCarry;
      const cfMaxDays = cfEnabled && form.cf_limit === "max_days" ? Number(form.cf_max_days) : null;
      await updateLeavePolicy(orgId, policy.policy_id, {
        name: policy.name,
        leave_type_id: policy.leave_type_id,
        effective_from: form.effective_from,
        rules_json: {
          ...rules,
          advanced_accrual_cf: monthlyExpire,
          monthly_unused_treatment: form.monthly_unused,
          annual_entitlement_days: annualDays,
          yearly_limit: annualDays,
          accrual_method: form.accrual_frequency === "annual" ? "yearly" : form.accrual_frequency,
          accrual: {
            ...(rules.accrual || {}),
            enabled: true,
            frequency: form.accrual_frequency,
            amount: calc.amount,
            timing: timing.value,
            accrual_day: timing.day,
            joining_cutoff_day: Number(form.joining_cutoff_day),
          },
          joining_cutoff_day: Number(form.joining_cutoff_day),
          carry_forward_allowed: cfEnabled,
          carry_forward: {
            ...(rules.carry_forward || {}),
            enabled: cfEnabled,
            decision: monthlyExpire ? "expire" : null,
            // "unlimited" stores no cap; a 0 cap would carry nothing in the leave engine.
            limit_type: cfMaxDays != null ? "days" : "none",
            max_days: cfMaxDays,
            expiry: form.cf_expiry,
            negative_balance: form.negative_balance,
            max_negative_days: form.negative_balance ? Number(form.max_negative_days || 0) : 0,
          },
          negative_balance: form.negative_balance,
          max_negative_days: form.negative_balance ? Number(form.max_negative_days || 0) : 0,
        },
      });
      setSaved(true);
    } catch (err) {
      setError(fmtErr(err, "Failed to save policy"));
      scrollToError();
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <div className="lss-form-page">
      <button type="button" className="lss-back-link" onClick={onClose}>
        <ArrowLeft size={15} /> Back to Accrual Policy
      </button>

      {saved ? (
        <SuccessScreen
          variant="panel"
          title="Accrual policy updated successfully!"
          message="Redirecting to accrual policies…"
          onComplete={() => onSaved?.(policy.policy_id)}
        />
      ) : loading ? (
        <div className="lss-form-card lss-form-state">
          <Loader2 size={18} className="als-spin" /> Loading accrual policy…
        </div>
      ) : loadError ? (
        <div className="lss-form-card lss-form-state lss-form-state--error">
          <div className="lss-error-bar">{loadError}</div>
          <button type="button" className="lss-btn-secondary" onClick={onClose}>
            <ArrowLeft size={14} /> Back to Accrual Policy
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="lss-form-card" noValidate>
          {error && (
            <div className="lss-form-error" ref={errorRef} role="alert">
              <div className="lss-error-bar">{error}</div>
            </div>
          )}

          {/* ── Section 1: Leave Type Information (read-only) — hidden for now.
          <section className="lss-form-section">
            <h2 className="lss-form-section-title">Leave Type Information</h2>
            <dl className="lss-info-grid">
              <div><dt>Policy Name</dt><dd>{policy.name}</dd></div>
              <div><dt>Leave Type</dt><dd>{leaveType ? `${leaveType.name} (${leaveType.code})` : "—"}</dd></div>
              <div><dt>Leave Code</dt><dd>{leaveType?.code || "—"}</dd></div>
              <div><dt>Category</dt><dd>{leaveType?.is_paid ? "Paid Leave" : "Unpaid Leave"}</dd></div>
              <div>
                <dt>Status</dt>
                <dd>
                  <span className={`als-status-pill ${leaveType?.is_active ? "als-status-pill--active" : "als-status-pill--inactive"}`}>
                    {leaveType?.is_active ? "Active" : "Inactive"}
                  </span>
                </dd>
              </div>
            </dl>
          </section>
          */}

          {/* ── Section 2: Accrual Configuration ── */}
          <section className="lss-form-section">
            <h2 className="lss-form-section-title">Accrual Configuration</h2>
            <div className="lss-form-grid lss-form-grid--3">
              <div className="lss-form-row">
                <label className="lss-label" htmlFor="ap-entitlement">
                  Annual Entitlement <span className="lss-required">*</span>
                </label>
                <div className="lss-input-with-unit lss-input-with-unit--grow">
                  <input
                    id="ap-entitlement"
                    className={`lss-input${fieldErrors.annual_entitlement ? " lss-input--error" : ""}`}
                    type="number"
                    inputMode="numeric"
                    min="1"
                    max="366"
                    step="1"
                    placeholder="e.g. 24"
                    value={form.annual_entitlement}
                    onKeyDown={blockNonIntegerKeys}
                    onChange={(e) => setField("annual_entitlement", digitsOnly(e.target.value))}
                    aria-invalid={Boolean(fieldErrors.annual_entitlement)}
                  />
                  <span className="lss-input-unit">days / year</span>
                </div>
                {fieldErrors.annual_entitlement && (
                  <span className="lss-field-error">{fieldErrors.annual_entitlement}</span>
                )}
              </div>
              <div className="lss-form-row">
                <label className="lss-label" htmlFor="ap-frequency">
                  Accrual Frequency <span className="lss-required">*</span>
                </label>
                <select
                  id="ap-frequency"
                  className="lss-input lss-select"
                  value={form.accrual_frequency}
                  onChange={(e) => setField("accrual_frequency", e.target.value)}
                >
                  {ACCRUAL_FREQUENCY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="lss-form-row">
                <span className="lss-label">Accrual Amount <span className="lss-required">*</span></span>
                <div
                  className={`lss-readonly-value${calc.amount != null && !calc.valid ? " lss-readonly-value--invalid" : ""}`}
                  aria-live="polite"
                >
                  {calc.amount == null
                    ? "Enter annual entitlement"
                    : calc.valid
                      ? `${fmtDays(calc.amount)} / ${periodNoun}`
                      : "Not allowed"}
                </div>
                <span className="lss-helper-text">
                  {calc.amount != null && !calc.valid
                    ? `Use a multiple of ${fmtDays(calc.unit)} for ${periodNoun}ly credits of 0.5, 1, 1.5 … days.`
                    : "Calculated automatically from Annual Entitlement and Accrual Frequency."}
                  {/* Working shown earlier — hidden for now:
                      `${fmtDays(form.annual_entitlement)} ÷ ${calc.periods} periods` */}
                </span>
              </div>

              <div className="lss-form-row">
                <label className="lss-label" htmlFor="ap-timing">Accrual Timing</label>
                <select
                  id="ap-timing"
                  className="lss-input lss-select"
                  value={form.accrual_timing}
                  onChange={(e) => setField("accrual_timing", e.target.value)}
                >
                  {ACCRUAL_TIMING_CHOICES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
                <span className="lss-helper-text lss-helper-text--strong">
                  {timing.hint} {periodNoun}
                  {calc.valid ? ` — ${fmtDays(calc.amount)} each time.` : "."}
                </span>
              </div>
              <div className="lss-form-row">
                <label className="lss-label" htmlFor="ap-cutoff">New Joiner Cutoff Day</label>
                <input
                  id="ap-cutoff"
                  className={`lss-input${fieldErrors.joining_cutoff_day ? " lss-input--error" : ""}`}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={28}
                  step={1}
                  value={form.joining_cutoff_day}
                  onKeyDown={blockNonIntegerKeys}
                  onChange={(e) => {
                    const v = digitsOnly(e.target.value);
                    setField("joining_cutoff_day", v === "" ? "" : Number(v));
                  }}
                  aria-invalid={Boolean(fieldErrors.joining_cutoff_day)}
                />
                {fieldErrors.joining_cutoff_day ? (
                  <span className="lss-field-error">{fieldErrors.joining_cutoff_day}</span>
                ) : (
                  <span className="lss-helper-text lss-helper-text--strong">
                    Joining on or before day {form.joining_cutoff_day || "—"}: full accrual for that month; after it: none.
                  </span>
                )}
              </div>
            </div>
          </section>

          {/* ── Section 3: Monthly Unused Leave Treatment ── */}
          <section className="lss-form-section">
            <h2 className="lss-form-section-title">Monthly Unused Leave Treatment</h2>
            <div className="lss-form-grid lss-form-grid--3">
              <div className="lss-form-row">
                <label className="lss-label" htmlFor="ap-monthly-unused">
                  Treatment <span className="lss-required">*</span>
                </label>
                <select
                  id="ap-monthly-unused"
                  className={`lss-input lss-select${fieldErrors.monthly_unused ? " lss-input--error" : ""}`}
                  value={form.monthly_unused}
                  onChange={(e) => setField("monthly_unused", e.target.value)}
                  aria-invalid={Boolean(fieldErrors.monthly_unused)}
                >
                  <option value="" disabled>Select treatment</option>
                  <option value="carry_forward">Carry forward</option>
                  <option value="expire">Expire</option>
                </select>
                {fieldErrors.monthly_unused ? (
                  <span className="lss-field-error">{fieldErrors.monthly_unused}</span>
                ) : monthlyExpire || monthlyCarry ? (
                  <span className="lss-helper-text lss-helper-text--strong">
                    {monthlyExpire
                      ? `Days not used by the end of each ${periodNoun} expire; they are not added to the next ${periodNoun}.`
                      : `Days not used in a ${periodNoun} stay in the balance and roll into the next ${periodNoun} until the leave year ends.`}
                  </span>
                ) : (
                  <span className="lss-helper-text">Choose what happens to leave not used in a {periodNoun}.</span>
                )}
              </div>
            </div>
          </section>

          {/* ── Section 4: Carry Forward & Balance Rules ── */}
          <section className="lss-form-section">
            <h2 className="lss-form-section-title">Carry Forward &amp; Balance Rules</h2>
            {!monthlyCarry ? (
              <p className="lss-helper-text lss-helper-text--strong">
                {monthlyExpire
                  ? `Unused leave expires every ${periodNoun}, so nothing is left to carry forward at the end of the leave year.`
                  : "Select a Monthly Unused Leave Treatment above to set carry forward rules."}
              </p>
            ) : (
              <div className="lss-rule-card">
                {/* Carry Forward toggle hidden — carry forward follows the Treatment choice above.
                <div className="lss-choice-row lss-choice-row--plain lss-choice-row--inline">
                  <span className="lss-label">Carry Forward</span>
                  <div className="lss-toggle-wrap">
                    <button
                      type="button"
                      role="switch"
                      aria-checked={form.cf_enabled}
                      aria-label="Carry Forward"
                      className={`lss-toggle ${form.cf_enabled ? "lss-toggle--on" : ""}`}
                      onClick={() => setField("cf_enabled", !form.cf_enabled)}
                    >
                      <span className="lss-toggle-knob" />
                    </button>
                    <span className="lss-toggle-label">{form.cf_enabled ? "Enabled" : "Disabled"}</span>
                  </div>
                </div>
                */}
                {/* Explanation hidden for now:
                <p className="lss-explain-box">
                  Unused days at the end of the leave cycle move to the next cycle, up to the maximum. Example: 10 unused days
                  with a 30-day maximum all carry over; 40 unused days carry 30 and lose 10.
                </p>
                */}
                {monthlyCarry && (
                  <div className="lss-form-grid lss-form-grid--3">
                    <div className="lss-form-row">
                      <label className="lss-label" htmlFor="ap-cf-limit">Carry Forward Limit</label>
                      <select
                        id="ap-cf-limit"
                        className="lss-input lss-select"
                        value={form.cf_limit}
                        onChange={(e) => setField("cf_limit", e.target.value)}
                      >
                        <option value="unlimited">Unlimited</option>
                        <option value="max_days">Maximum days</option>
                      </select>
                    </div>
                    {form.cf_limit === "max_days" && (
                      <div className="lss-form-row">
                        <label className="lss-label" htmlFor="ap-cf-max">
                          Maximum Carry Forward <span className="lss-required">*</span>
                        </label>
                        <div className="lss-input-with-unit lss-input-with-unit--grow">
                          <input
                            id="ap-cf-max"
                            className={`lss-input${fieldErrors.cf_max_days ? " lss-input--error" : ""}`}
                            type="number"
                            min={0.5}
                            step="0.5"
                            placeholder="e.g. 30"
                            value={form.cf_max_days}
                            onChange={(e) => setField("cf_max_days", e.target.value)}
                            aria-invalid={Boolean(fieldErrors.cf_max_days)}
                          />
                          <span className="lss-input-unit">days</span>
                        </div>
                        {fieldErrors.cf_max_days && <span className="lss-field-error">{fieldErrors.cf_max_days}</span>}
                      </div>
                    )}
                    <div className="lss-form-row">
                      <label className="lss-label" htmlFor="ap-cf-expiry">Expiry</label>
                      <select
                        id="ap-cf-expiry"
                        className="lss-input lss-select"
                        value={form.cf_expiry}
                        onChange={(e) => setField("cf_expiry", e.target.value)}
                      >
                        {CF_EXPIRY_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Negative Balance — hidden for now; saved values are kept unchanged on save.
            <div className="lss-rule-card">
              <div className="lss-choice-row lss-choice-row--plain">
                <span className="lss-label">Negative Balance</span>
                <div className="lss-toggle-wrap">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={form.negative_balance}
                    aria-label="Negative Balance"
                    className={`lss-toggle ${form.negative_balance ? "lss-toggle--on" : ""}`}
                    onClick={() => setField("negative_balance", !form.negative_balance)}
                  >
                    <span className="lss-toggle-knob" />
                  </button>
                  <span className="lss-toggle-label">{form.negative_balance ? "Allowed" : "Not Allowed"}</span>
                </div>
              </div>
              <p className="lss-explain-box">
                Employees can apply even at 0 balance; the balance goes negative and is recovered from future accruals.
                Example: 0 balance, 2 days taken → −2; after a 1.5-day accrual → −0.5.
              </p>
              {form.negative_balance && (
                <div className="lss-form-row">
                  <label className="lss-label" htmlFor="ap-neg-max">Maximum Negative Balance</label>
                  <div className="lss-input-with-unit lss-input-with-unit--grow">
                    <input
                      id="ap-neg-max"
                      className="lss-input"
                      type="number"
                      min={0}
                      step="1"
                      value={form.max_negative_days}
                      onChange={(e) => setField("max_negative_days", e.target.value)}
                    />
                    <span className="lss-input-unit">days</span>
                  </div>
                </div>
              )}
            </div>
            */}
          </section>

          <div className="lss-form-actions">
            <button type="button" className="lss-btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="lss-btn-primary" disabled={saving}>
              {saving ? <Loader2 size={14} className="als-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

function Pagination({ page, total, pageSize, onPage }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  return (
    <div className="als-pagination">
      <span className="als-pagination-info">
        Showing {Math.min((page - 1) * pageSize + 1, total)} to{" "}
        {Math.min(page * pageSize, total)} of {total} entries
      </span>
      <div className="als-pagination-controls">
        <select className="als-select-sm" value={pageSize} onChange={() => {}}>
          <option value={10}>10 per page</option>
        </select>
        <button
          type="button"
          className="als-page-btn"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          ‹
        </button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .slice(Math.max(0, page - 2), Math.min(totalPages, page + 1))
          .map((p) => (
            <button
              key={p}
              type="button"
              className={`als-page-btn ${p === page ? "als-page-btn--active" : ""}`}
              onClick={() => onPage(p)}
            >
              {p}
            </button>
          ))}
        <button
          type="button"
          className="als-page-btn"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          ›
        </button>
      </div>
    </div>
  );
}

// ─── Tab 1 – Leave Types ──────────────────────────────────────────────────────

function LeaveTypesTab({ orgId, onAdd, onEdit, highlightId }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState(null);
  const PAGE_SIZE = 10;

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [types, policies] = await Promise.all([
        fetchLeaveTypes(orgId),
        fetchLeavePolicies(orgId).catch(() => []),
      ]);
      setItems(enrichLeaveTypesWithPolicies(types, policies));
    } catch (e) {
      toast.error(fmtErr(e, "Failed to load leave types"));
    } finally {
      setLoading(false);
    }
  }, [orgId, toast]);

  useEffect(() => { load(); }, [load]);

  // After returning from the Add/Edit form, jump to the page holding the saved row.
  useEffect(() => {
    if (highlightId == null || loading) return;
    const idx = items.findIndex((r) => String(r.leave_type_id) === String(highlightId));
    if (idx >= 0) setPage(Math.floor(idx / PAGE_SIZE) + 1);
  }, [highlightId, items, loading]);

  const handleDelete = async (row) => {
    const ok = window.confirm(
      `Delete leave type "${row.name}" (${row.code})?\n\nThis archives the leave type so it no longer appears for new requests. Existing balances and history are kept.`
    );
    if (!ok) return;
    setDeletingId(row.leave_type_id);
    try {
      await deleteLeaveType(orgId, row.leave_type_id);
      toast.success(`Leave type "${row.code}" deleted.`);
      setItems((prev) => prev.filter((r) => r.leave_type_id !== row.leave_type_id));
    } catch (e) {
      toast.error(fmtErr(e, "Failed to delete leave type"));
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleActive = async (row) => {
    try {
      await updateLeaveType(orgId, row.leave_type_id, { is_active: !row.is_active });
      toast.success(`Leave type "${row.name}" ${!row.is_active ? "activated" : "deactivated"}.`);
      setItems((prev) =>
        prev.map((r) =>
          r.leave_type_id === row.leave_type_id ? { ...r, is_active: !r.is_active } : r
        )
      );
    } catch (e) {
      toast.error(fmtErr(e, "Failed to update status"));
    }
  };

  const paged = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="als-tab-body">
      <div className="lss-toolbar">
        <div className="als-info-bar">
          <Info size={14} />
          Click on the status badge to toggle a leave type between Active and Inactive.
        </div>
        <button type="button" className="lss-btn-primary lss-toolbar-btn" onClick={onAdd}>
          <Plus size={15} />
          Add Leave Type
        </button>
      </div>

      <div className="als-table-wrap">
        <table className="als-table">
          <thead>
            <tr>
              <th>Leave Code</th>
              <th>Leave Name</th>
              <th>Description</th>
              <th>Paid Leave</th>
              <th>Annual Limit (Days)</th>
              <th>Applicable To</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="als-table-empty">
                  <Loader2 size={18} className="als-spin" /> Loading leave types…
                </td>
              </tr>
            ) : paged.length === 0 ? (
              <>
                <tr>
                  <td colSpan={8} className="als-table-empty">
                    No leave types configured yet. Click &quot;Add Leave Type&quot; to create one.
                  </td>
                </tr>
              </>
            ) : (
              paged.map((row) => {
                const appLabel = formatApplicableLabel(row.applicability_json?.applicable_to);
                return (
                  <tr
                    key={row.leave_type_id}
                    className={String(row.leave_type_id) === String(highlightId) ? "lss-row-new" : ""}
                  >
                    <td className="als-td-code">{row.code}</td>
                    <td className="als-td-name lss-td-wrap">{row.name}</td>
                    <td className="lss-td-desc" title={row.description || undefined}>{row.description || "—"}</td>
                    <td>
                      <span className={`lss-badge ${row.is_paid ? "lss-badge--yes" : "lss-badge--no"}`}>
                        {row.is_paid ? "Yes" : "No"}
                      </span>
                    </td>
                    <td>
                      {row.annual_limit_days != null ? row.annual_limit_days : "—"}
                    </td>
                    <td>{appLabel}</td>
                    <td>
                      <button
                        type="button"
                        className={`als-status-pill als-status-pill--btn ${
                          row.is_active ? "als-status-pill--active" : "als-status-pill--inactive"
                        }`}
                        onClick={() => handleToggleActive(row)}
                        title="Click to toggle status"
                      >
                        {row.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td>
                      <div className="als-action-btns">
                        <button
                          type="button"
                          className="als-icon-btn als-icon-btn--edit"
                          title="Edit"
                          onClick={() => onEdit(row.leave_type_id)}
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          className="als-icon-btn als-icon-btn--delete"
                          title="Delete"
                          disabled={deletingId === row.leave_type_id}
                          onClick={() => handleDelete(row)}
                        >
                          {deletingId === row.leave_type_id ? (
                            <Loader2 size={14} className="als-spin" />
                          ) : (
                            <Trash2 size={14} />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} total={items.length} pageSize={PAGE_SIZE} onPage={setPage} />
    </div>
  );
}

// ─── Tab 2 – Leave Policies ───────────────────────────────────────────────────

function LeavePoliciesTab({ orgId, onEdit, highlightId }) {
  const toast = useToast();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [showGuide, setShowGuide] = useState(false);
  const PAGE_SIZE = 10;

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [types, pols] = await Promise.all([
        fetchLeaveTypes(orgId),
        fetchLeavePolicies(orgId),
      ]);
      const activeTypes = Array.isArray(types) ? types : [];
      const allPols = Array.isArray(pols) ? pols : [];
      setLeaveTypes(activeTypes);

      // Auto-ensure: create missing policies for leave types that don't have one
      const polTypeIds = new Set(allPols.map((p) => String(p.leave_type_id)));
      const missing = activeTypes.filter((t) => !polTypeIds.has(String(t.leave_type_id)));
      const created = [];
      for (const lt of missing) {
        try {
          // Placeholder only — the admin sets frequency, amount and entitlement when configuring.
          const np = await createLeavePolicy(orgId, {
            name: `${lt.name} Accrual Policy`,
            leave_type_id: lt.leave_type_id,
            effective_from: `${new Date().getFullYear()}-01-01`,
            status: "draft",
            rules_json: {},
          });
          created.push(np);
        } catch {}
      }
      // Only show policies for leave types that still exist (not archived/deleted)
      const validTypeIds = new Set(activeTypes.map((t) => String(t.leave_type_id)));
      setPolicies(
        [...allPols, ...created].filter(
          (p) => validTypeIds.has(String(p.leave_type_id))
        )
      );
    } catch (e) {
      toast.error(fmtErr(e, "Failed to load accrual policies"));
    } finally {
      setLoading(false);
    }
  }, [orgId, toast]);

  useEffect(() => { load(); }, [load]);

  const getType = (id) =>
    leaveTypes.find((t) => String(t.leave_type_id) === String(id));

  const getTypeName = (id) => {
    const lt = getType(id);
    return lt ? `${lt.name} (${lt.code})` : "—";
  };

  const resolveFrequency = (rules) => {
    const acc = rules?.accrual || {};
    const freq = acc.frequency || rules?.accrual_method;
    const match = ACCRUAL_FREQUENCY_OPTIONS.find((o) => o.value === freq);
    if (match) return match.label;
    const match2 = ACCRUAL_OPTIONS.find((a) => a.value === freq);
    return match2 ? match2.label : freq || "—";
  };

  const filtered = policies.filter((p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const typeName = getTypeName(p.leave_type_id).toLowerCase();
    return (
      (p.name || "").toLowerCase().includes(q) || typeName.includes(q)
    );
  });

  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const hasNoLeaveTypes = !loading && leaveTypes.length === 0;

  return (
    <div className="als-tab-body">
      {/* ── Collapsible Guidance ── */}
      <div className="lss-guide-card">
        <button
          type="button"
          className="lss-guide-card-toggle"
          onClick={() => setShowGuide((v) => !v)}
        >
          <div className="lss-guide-card-toggle-left">
            <Info size={15} />
            <strong>How to configure Accrual Policy</strong>
          </div>
          <ChevronDown size={16} className={`lss-guide-chevron ${showGuide ? "lss-guide-chevron--open" : ""}`} />
        </button>
        {showGuide && (
          <div className="lss-guide-card-body">
            <div className="lss-guide-step-list">
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">1</span>
                <div>
                  <strong>Create a Leave Type</strong>
                  <p>Go to the <em>Leave Types</em> tab and add a new leave type (e.g. Casual Leave, Sick Leave). An accrual policy is automatically created for it.</p>
                </div>
              </div>
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">2</span>
                <div>
                  <strong>Open the Accrual Policy</strong>
                  <p>Find your leave type in the table below and click the <em>Edit</em> (pencil) icon to open the policy configuration.</p>
                </div>
              </div>
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">3</span>
                <div>
                  <strong>Set Annual Entitlement &amp; Frequency</strong>
                  <p>Enter the days per year (e.g. 24) and choose how often leave is earned (Monthly, Quarterly, Half Yearly, Annual). The accrual amount is calculated automatically (e.g. 24 days monthly = 2 days/month).</p>
                </div>
              </div>
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">4</span>
                <div>
                  <strong>Configure New Joiner Rule</strong>
                  <p>Set the cutoff day (default: 15th). Employees joining on or before the 15th get accrual for that month. After the 15th, accrual starts from the next month.</p>
                </div>
              </div>
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">5</span>
                <div>
                  <strong>Enable Carry Forward (optional)</strong>
                  <p>Allow unused leave to carry over to the next cycle. Set a maximum (e.g. 30 days) and expiry (e.g. end of next cycle).</p>
                </div>
              </div>
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">6</span>
                <div>
                  <strong>Save the Policy</strong>
                  <p>Click <em>Save Changes</em>. The system will use this configuration for future accrual processing and employee leave balance calculations.</p>
                </div>
              </div>
            </div>
            <p className="lss-guide-note" style={{ marginTop: 10 }}>
              Accrual policies cannot be created or deleted independently. Each policy is tied to a leave type and managed from this page.
            </p>
          </div>
        )}
      </div>

      {hasNoLeaveTypes ? (
        <div className="als-table-wrap lss-policies-table-wrap">
          <table className="als-table lss-policies-table">
            <thead>
              <tr>
                <th>Leave Type</th>
                <th>Accrual<br />Frequency</th>
                <th>Accrual<br />Rate</th>
                <th>Annual<br />Entitlement</th>
                <th>Carry<br />Forward</th>
                <th>Max Carry<br />Forward</th>
                <th>Status</th>
                <th className="lss-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={8} className="als-table-empty">
                  No leave types configured yet. Create a leave type first to configure its accrual policy.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="lss-search-bar">
            <input
              className="lss-input"
              type="text"
              placeholder="Search by policy or leave type..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>

          <div className="als-table-wrap lss-policies-table-wrap">
            <table className="als-table lss-policies-table">
              <thead>
                <tr>
                  <th>Leave Type</th>
                  <th>Accrual<br />Frequency</th>
                  <th>Accrual<br />Rate</th>
                  <th>Annual<br />Entitlement</th>
                  <th>Carry<br />Forward</th>
                  <th>Max Carry<br />Forward</th>
                  <th>Status</th>
                  <th className="lss-th-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="als-table-empty">
                      <Loader2 size={18} className="als-spin" /> Loading accrual policies…
                    </td>
                  </tr>
                ) : paged.length === 0 ? (
                  <>
                    <tr>
                      <td colSpan={8} className="als-table-empty">
                        {search
                          ? "No matching policies found."
                          : "No accrual policies found. Policies are created automatically when you add a leave type."}
                      </td>
                    </tr>
                  </>
                ) : (
                  paged.map((row) => {
                    const r = row.rules_json || {};
                    const acc = r.accrual || {};
                    const notConfigured = row.status === "draft" && acc.amount == null && acc.rate_per_month == null;
                    const amt = acc.amount ?? acc.rate_per_month;
                    const freq = acc.frequency || r.accrual_method || "monthly";
                    const annualDays = r.annual_entitlement_days ?? r.yearly_limit ?? (amt != null ? (() => {
                      if (freq === "monthly") return Number(amt) * 12;
                      if (freq === "quarterly") return Number(amt) * 4;
                      if (freq === "half_yearly") return Number(amt) * 2;
                      return Number(amt);
                    })() : null);
                    const cfEnabled = r.carry_forward?.enabled || r.carry_forward_allowed;
                    return (
                      <tr
                        key={row.policy_id}
                        className={[
                          (() => { const lt = getType(row.leave_type_id); return lt && lt.is_active === false ? "lss-row-inactive" : ""; })(),
                          String(row.policy_id) === String(highlightId) ? "lss-row-new" : "",
                        ].filter(Boolean).join(" ")}
                      >
                        <td className="als-td-name">{getTypeName(row.leave_type_id)}</td>
                        <td>{notConfigured ? "—" : resolveFrequency(r)}</td>
                        <td>{amt != null ? `${Number(amt).toFixed(2)} days` : "—"}</td>
                        <td>{annualDays != null ? `${Number(annualDays).toFixed(1)} days/year` : "—"}</td>
                        <td>
                          <span className={`lss-badge ${cfEnabled ? "lss-badge--yes" : "lss-badge--no"}`}>
                            {cfEnabled ? "Enabled" : "Disabled"}
                          </span>
                        </td>
                        <td>{cfEnabled ? `${r.carry_forward?.max_days ?? 0} days` : "—"}</td>
                        <td>
                          {(() => {
                            const lt = getType(row.leave_type_id);
                            const ltInactive = lt && lt.is_active === false;
                            const isDraft = row.status === "draft";
                            const pillClass = ltInactive
                              ? "als-status-pill--inactive"
                              : isDraft
                                ? "als-status-pill--draft"
                                : row.is_active
                                  ? "als-status-pill--active"
                                  : "als-status-pill--inactive";
                            const pillLabel = ltInactive
                              ? "Inactive"
                              : isDraft
                                ? "Not Configured"
                                : row.is_active
                                  ? "Active"
                                  : "Inactive";
                            return (
                              <span className={`als-status-pill ${pillClass}`}>
                                {pillLabel}
                              </span>
                            );
                          })()}
                        </td>
                        <td>
                          <div className="als-action-btns">
                            <button
                              type="button"
                              className="als-icon-btn als-icon-btn--edit"
                              title={row.status === "draft" ? "Configure Policy" : "View / Edit"}
                              onClick={() => onEdit(row.policy_id)}
                            >
                              <Pencil size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onPage={setPage} />
        </>
      )}

    </div>
  );
}

// ─── Tab 3 – Leave Approval Workflow ─────────────────────────────────────────

const DEFAULT_WF_FORM = {
  approvalRequired: true,
  level2Enabled: false,
  threshold: 5,
  name: "",
  level1Approver: "reporting_manager",
  level2Approver: "",
};

// Approver values: "reporting_manager" (employee's manager in the org hierarchy),
// "role:<role_id>" (anyone assigned that organisation role), or legacy "hr" (HR login role).
const APPROVER_REPORTING_MANAGER = "reporting_manager";
const APPROVER_LEGACY_HR = "hr";

function approverValueFromLevel(level, slot) {
  const type = String(level?.approver_type || "").toLowerCase();
  if (type === "role" && level?.approver_role_id) return `role:${level.approver_role_id}`;
  if (type === APPROVER_REPORTING_MANAGER) return APPROVER_REPORTING_MANAGER;
  if (type === APPROVER_LEGACY_HR) return APPROVER_LEGACY_HR;
  return slot === "level1" ? APPROVER_REPORTING_MANAGER : APPROVER_LEGACY_HR;
}

function levelFieldsFromApprover(value, roles, knownNames = {}) {
  if (value && value.startsWith("role:")) {
    const roleId = value.slice(5);
    const role = (roles || []).find((r) => String(r.role_id) === roleId);
    return {
      approver_type: "role",
      approver_role_id: roleId,
      approver_role_name: role?.name || knownNames[roleId] || null,
    };
  }
  if (value === APPROVER_LEGACY_HR) return { approver_type: APPROVER_LEGACY_HR };
  return { approver_type: APPROVER_REPORTING_MANAGER };
}

/** "Super Admin (Sai Kiran) / HR" — names of the org's active admin logins, when known. */
function superAdminApproverLabel(adminNames) {
  if (!adminNames?.length) return "Super Admin / HR";
  const shown = adminNames.slice(0, 2).join(", ");
  const more = adminNames.length > 2 ? ` +${adminNames.length - 2}` : "";
  return `Super Admin (${shown}${more}) / HR`;
}

/** Active admin (Super Admin) login names — they can approve "Super Admin / HR" steps. */
function useAdminApproverNames(orgId) {
  const [names, setNames] = useState([]);
  useEffect(() => {
    let alive = true;
    fetchIamUsers({ role: "admin", access: "login" })
      .then((rows) => {
        if (!alive) return;
        const list = (Array.isArray(rows) ? rows : [])
          .filter((u) => u.has_login && String(u.role || "").toLowerCase() === "admin")
          .filter((u) => !["inactive", "suspended"].includes(String(u.status || "active").toLowerCase()))
          .map((u) => u.full_name || u.username || u.email)
          .filter(Boolean);
        setNames([...new Set(list)]);
      })
      .catch(() => { if (alive) setNames([]); });
    return () => { alive = false; };
  }, [orgId]);
  return names;
}

function approverLabel(value, roles, knownNames = {}, adminNames = []) {
  if (!value || value === APPROVER_REPORTING_MANAGER) return "Reporting Manager";
  if (value === APPROVER_LEGACY_HR) return superAdminApproverLabel(adminNames);
  const roleId = value.slice(5);
  const role = (roles || []).find((r) => String(r.role_id) === roleId);
  return role?.name || knownNames[roleId] || "Selected role";
}

function workflowDisplayName(leaveType, cfg = {}) {
  if (cfg.name) return cfg.name;
  const code = leaveType?.code || leaveType?.name || "Leave";
  return `${code} Approval Workflow`;
}

function parseWorkflowForm(leaveType) {
  const cfg = leaveType?.approval_config_json || {};
  const app = leaveType?.applicability_json || {};
  const approvalRequired =
    app.approval_required !== false &&
    app.requires_approval !== false &&
    cfg.approval_required !== false;
  const levels = Array.isArray(cfg.levels) ? cfg.levels : [];
  const l2 =
    levels.find((l) => String(l?.role || "").toLowerCase().includes("hr")) ||
    levels[1] ||
    null;
  const level2Enabled = Boolean(l2) && l2.enabled !== false;
  const cond = l2?.condition && typeof l2.condition === "object" ? l2.condition : null;
  let threshold = 5;
  if (cond?.value != null && cond.value !== "") {
    threshold = Number(cond.value);
  } else if (level2Enabled && !cond) {
    threshold = 0;
  }
  if (Number.isNaN(threshold) || threshold < 0) threshold = 5;
  const l1 = levels.find((l) => String(l?.role || "").toLowerCase() === "manager") || levels[0] || {};
  const level1Approver = approverValueFromLevel(l1, "level1");
  const level2Approver = approverValueFromLevel(l2 || {}, "level2");
  const roleNames = {};
  for (const l of [l1, l2]) {
    if (l?.approver_role_id && l?.approver_role_name) roleNames[l.approver_role_id] = l.approver_role_name;
  }
  return {
    roleNames,
    approvalRequired,
    level2Enabled,
    threshold,
    name: workflowDisplayName(leaveType, cfg),
    level1Approver,
    level2Approver,
  };
}

function buildApprovalConfigPayload(form, roles) {
  const names = form.roleNames || {};
  return {
    name: (form.name || "").trim() || "Approval Workflow",
    approval_required: form.approvalRequired !== false,
    levels: [
      {
        role: "manager",
        required: true,
        ...levelFieldsFromApprover(form.level1Approver || APPROVER_REPORTING_MANAGER, roles, names),
      },
      {
        role: "hr",
        ...levelFieldsFromApprover(form.level2Approver || APPROVER_LEGACY_HR, roles, names),
        enabled: Boolean(form.level2Enabled),
        condition: {
          field: "duration_days",
          operator: ">",
          value: Number(form.threshold) || 5,
        },
      },
    ],
  };
}

function WorkflowPath({ level1Label, level2Label }) {
  const nodes = level2Label
    ? ["Employee", level1Label, level2Label, "Final Decision"]
    : ["Employee", level1Label, "Final Decision"];
  return (
    <div className="lss-wf-path" aria-hidden="true">
      <div className="lss-wf-path-title">Approval Flow</div>
      <div className="lss-wf-path-row">
        {nodes.map((label, i) => (
          <div key={`${i}-${label}`} className="lss-wf-path-item">
            <div className="lss-wf-node">
              <User size={14} />
              <span>{label}</span>
            </div>
            {i < nodes.length - 1 && (
              <ArrowRight size={16} className="lss-wf-arrow" />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Org roles for approver dropdowns; inactive roles are only listed when already selected. */
function RoleApproverOptions({ roles, current }) {
  return (roles || [])
    .filter((r) => (r.status || "active") === "active" || `role:${r.role_id}` === current)
    .map((r) => (
      <option key={r.role_id} value={`role:${r.role_id}`}>
        {r.name}
        {(r.status || "active") !== "active" ? " (inactive)" : ""}
        {r.assigned_user_count === 0 ? " — no users assigned" : ""}
      </option>
    ));
}

/** Full-page Edit Workflow form (replaces the former modal). */
function WorkflowFormPage({ orgId, leaveTypeId, onClose, onSaved }) {
  const [saved, setSaved] = useState(false);
  const adminNames = useAdminApproverNames(orgId);
  const [leaveType, setLeaveType] = useState(null);
  const [form, setForm] = useState(DEFAULT_WF_FORM);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const savingRef = useRef(false);
  const errorRef = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchIamRoles()
      .then((data) => { if (alive) setRoles(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setRoles([]); });
    return () => { alive = false; };
  }, [orgId]);

  useEffect(() => {
    if (!orgId || !leaveTypeId) return undefined;
    let alive = true;
    setLoading(true);
    setLoadError("");
    fetchLeaveTypes(orgId)
      .then((types) => {
        if (!alive) return;
        const found = (types || []).find((t) => String(t.leave_type_id) === String(leaveTypeId));
        if (!found) {
          setLoadError("This leave type could not be found. It may have been deleted.");
          return;
        }
        setLeaveType(found);
        setForm(parseWorkflowForm(found));
      })
      .catch((e) => { if (alive) setLoadError(fmtErr(e, "Failed to load workflow")); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orgId, leaveTypeId]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((fe) => (fe[k] ? { ...fe, [k]: undefined } : fe));
  };

  const scrollToError = () =>
    requestAnimationFrame(() => errorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }));

  const toggleLevel2 = () =>
    setForm((f) => {
      const turningOn = !f.level2Enabled;
      const needsDefault = !f.level2Approver;
      return {
        ...f,
        level2Enabled: turningOn,
        level2Approver:
          turningOn && needsDefault ? APPROVER_LEGACY_HR : f.level2Approver,
        threshold: turningOn && (f.threshold === 0 || f.threshold == null) ? 5 : f.threshold,
      };
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingRef.current || !leaveType) return;
    const errs = {};
    if (!form.name.trim()) errs.name = "Workflow name is required.";
    if (form.level2Enabled && !form.level2Approver) errs.level2Approver = "Select the Level 2 approver.";
    if (form.level2Enabled) {
      const n = Number(form.threshold);
      if (form.threshold === "" || Number.isNaN(n) || n < 0) errs.threshold = "Enter 0 or more days.";
    }
    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      setError("Please fix the highlighted fields.");
      scrollToError();
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError("");
    try {
      await updateLeaveType(orgId, leaveType.leave_type_id, {
        approval_config_json: buildApprovalConfigPayload(form, roles),
      });
      setSaved(true);
    } catch (err) {
      setError(fmtErr(err, "Failed to save workflow"));
      scrollToError();
      savingRef.current = false;
      setSaving(false);
    }
  };

  const level1Label = approverLabel(form.level1Approver, roles, form.roleNames, adminNames);
  const level2Label =
    form.level2Enabled && form.level2Approver
      ? approverLabel(form.level2Approver, roles, form.roleNames, adminNames)
      : null;

  return (
    <div className="lss-form-page">
      <button type="button" className="lss-back-link" onClick={onClose}>
        <ArrowLeft size={15} /> Back to Workflow &amp; Approval
      </button>

      {saved ? (
        <SuccessScreen
          variant="panel"
          title="Approval workflow saved successfully!"
          message="Redirecting to workflow & approval…"
          onComplete={() => onSaved?.(leaveType.leave_type_id)}
        />
      ) : loading ? (
        <div className="lss-form-card lss-form-state">
          <Loader2 size={18} className="als-spin" /> Loading workflow…
        </div>
      ) : loadError ? (
        <div className="lss-form-card lss-form-state lss-form-state--error">
          <div className="lss-error-bar">{loadError}</div>
          <button type="button" className="lss-btn-secondary" onClick={onClose}>
            <ArrowLeft size={14} /> Back to Workflow &amp; Approval
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="lss-form-card" noValidate>
          {error && (
            <div className="lss-form-error" ref={errorRef} role="alert">
              <div className="lss-error-bar">{error}</div>
            </div>
          )}

          {/* ── Section 1: Workflow Details ── */}
          <section className="lss-form-section">
            <h2 className="lss-form-section-title">Workflow Details</h2>
            <div className="lss-form-grid">
              <div className="lss-form-row">
                <span className="lss-label">Leave Type</span>
                <div className="lss-readonly-value lss-readonly-value--plain">
                  {leaveType.name}{leaveType.code ? ` (${leaveType.code})` : ""}
                </div>
              </div>
              <div className="lss-form-row">
                <label className="lss-label" htmlFor="wf-name">
                  Workflow Name <span className="lss-required">*</span>
                </label>
                <input
                  id="wf-name"
                  className={`lss-input${fieldErrors.name ? " lss-input--error" : ""}`}
                  placeholder="e.g. EL Approval Workflow"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  aria-invalid={Boolean(fieldErrors.name)}
                />
                {fieldErrors.name && <span className="lss-field-error">{fieldErrors.name}</span>}
              </div>
            </div>
          </section>

          {/* ── Section 2: Approval Levels ── */}
          <section className="lss-form-section">
            <h2 className="lss-form-section-title">Approval Levels</h2>
            <div className="lss-form-grid lss-form-grid--3">
              <div className="lss-form-row">
                <label className="lss-label" htmlFor="wf-l1">Level 1 Approver</label>
                <select
                  id="wf-l1"
                  className="lss-input lss-select"
                  value={form.level1Approver || APPROVER_REPORTING_MANAGER}
                  onChange={(e) => setField("level1Approver", e.target.value)}
                >
                  <option value={APPROVER_REPORTING_MANAGER}>Reporting Manager (from org hierarchy)</option>
                  {roles?.length > 0 && (
                    <optgroup label="Organization roles">
                      <RoleApproverOptions roles={roles} current={form.level1Approver} />
                    </optgroup>
                  )}
                </select>
              </div>
              <div className="lss-form-row lss-form-row--span2 lss-form-row--shift">
                <span className="lss-label">Level 2 Approval Required</span>
                <div className="lss-toggle-wrap lss-toggle-wrap--field">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={form.level2Enabled}
                    aria-label="Level 2 Approval Required"
                    className={`lss-toggle ${form.level2Enabled ? "lss-toggle--on" : ""}`}
                    onClick={toggleLevel2}
                  >
                    <span className="lss-toggle-knob" />
                  </button>
                  <span className="lss-toggle-label">{form.level2Enabled ? "Yes" : "No"}</span>
                </div>
              </div>

              {form.level2Enabled && (
                <>
                  <div className="lss-form-row">
                    <label className="lss-label" htmlFor="wf-l2">
                      Level 2 Approver <span className="lss-required">*</span>
                    </label>
                    <select
                      id="wf-l2"
                      className={`lss-input lss-select${fieldErrors.level2Approver ? " lss-input--error" : ""}`}
                      value={form.level2Approver || ""}
                      onChange={(e) => setField("level2Approver", e.target.value)}
                    >
                      <option value="" disabled>Select approver</option>
                      <option value={APPROVER_LEGACY_HR}>{superAdminApproverLabel(adminNames)} (login role)</option>
                      <option value={APPROVER_REPORTING_MANAGER}>Reporting Manager (from org hierarchy)</option>
                      {roles?.length > 0 && (
                        <optgroup label="Organization roles">
                          <RoleApproverOptions roles={roles} current={form.level2Approver} />
                        </optgroup>
                      )}
                    </select>
                    {fieldErrors.level2Approver && (
                      <span className="lss-field-error">{fieldErrors.level2Approver}</span>
                    )}
                  </div>
                  <div className="lss-form-row lss-form-row--span2 lss-form-row--shift">
                    <label className="lss-label" htmlFor="wf-threshold">
                      Condition <span className="lss-required">*</span>
                    </label>
                    <div className="lss-wf-condition">
                      <span>Leave Duration &gt;</span>
                      <input
                        id="wf-threshold"
                        className={`lss-input lss-wf-days${fieldErrors.threshold ? " lss-input--error" : ""}`}
                        type="number"
                        min={0}
                        step="0.5"
                        value={form.threshold}
                        onChange={(e) => {
                          const v = e.target.value;
                          setField("threshold", v === "" ? "" : Number(v));
                        }}
                      />
                      <span>Days</span>
                    </div>
                    {fieldErrors.threshold ? (
                      <span className="lss-field-error">{fieldErrors.threshold}</span>
                    ) : (
                      <span className="lss-helper-text lss-helper-text--strong">
                        Requests longer than {form.threshold === "" ? "—" : form.threshold} days also need Level 2 approval.
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </section>

          {/* ── Section 3: Approval Flow preview ── */}
          <section className="lss-form-section">
            <WorkflowPath level1Label={level1Label} level2Label={level2Label} />
          </section>

          <div className="lss-form-actions">
            <button type="button" className="lss-btn-secondary" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="lss-btn-primary" disabled={saving}>
              {saving ? <Loader2 size={14} className="als-spin" /> : <Save size={14} />}
              {saving ? "Saving…" : "Save Workflow"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function LeaveApprovalWorkflowTab({ orgId, onEdit, highlightId }) {
  const toast = useToast();
  const adminNames = useAdminApproverNames(orgId);
  const [, setSearchParams] = useSearchParams();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [showGuide, setShowGuide] = useState(false);
  const [roles, setRoles] = useState([]);
  // leave_type_id → true (accrual policy configured) / false (only a draft). Missing = legacy, no policy rows.
  const [policyConfigured, setPolicyConfigured] = useState({});
  const PAGE_SIZE = 10;

  useEffect(() => {
    let alive = true;
    fetchIamRoles()
      .then((data) => { if (alive) setRoles(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setRoles([]); });
    return () => { alive = false; };
  }, [orgId]);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [types, pols] = await Promise.all([fetchLeaveTypes(orgId), fetchLeavePolicies(orgId)]);
      setLeaveTypes(types || []);
      const byType = {};
      for (const p of Array.isArray(pols) ? pols : []) {
        const id = String(p.leave_type_id);
        const configured = p.is_active && p.status !== "draft";
        byType[id] = Boolean(byType[id] || configured);
      }
      setPolicyConfigured(byType);
    } catch (e) {
      toast.error(fmtErr(e, "Failed to load workflows"));
    } finally {
      setLoading(false);
    }
  }, [orgId, toast]);

  useEffect(() => { load(); }, [load]);

  const rows = leaveTypes.map((t) => {
    const parsed = parseWorkflowForm(t);
    return {
      leaveType: t,
      name: parsed.name,
      level1: approverLabel(parsed.level1Approver, roles, parsed.roleNames, adminNames),
      level2: parsed.level2Enabled ? approverLabel(parsed.level2Approver, roles, parsed.roleNames, adminNames) : "",
      level2Required: parsed.level2Enabled,
      isActive: t.is_active !== false,
      // The workflow only becomes active once the accrual policy is configured.
      policyPending: policyConfigured[String(t.leave_type_id)] === false,
    };
  });

  const paged = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="als-tab-body">
      {/* Create Workflow hidden — every leave type already gets a workflow; admins change approvers via Edit
          (the Edit Workflow page has no create mode; add one before restoring this button).
      <button type="button" className="als-btn-primary als-ml-auto">
        <Plus size={14} />
        Create Workflow
      </button>
      */}

      <div className="lss-guide-card">
        <button
          type="button"
          className="lss-guide-card-toggle"
          onClick={() => setShowGuide((v) => !v)}
        >
          <div className="lss-guide-card-toggle-left">
            <Info size={15} />
            <strong>How Approval Workflows Work</strong>
          </div>
          <ChevronDown size={16} className={`lss-guide-chevron ${showGuide ? "lss-guide-chevron--open" : ""}`} />
        </button>
        {showGuide && (
          <div className="lss-guide-card-body">
            <div className="lss-guide-step-list">
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num" aria-hidden="true">
                  <Pencil size={11} />
                </span>
                <div>
                  <strong>Change who approves a leave type</strong>
                  <p>
                    Click the <strong>Edit</strong> button in the <strong>Actions</strong> column. There you can pick the{" "}
                    <strong>Level 1 Approver</strong> (Reporting Manager or an organization role), turn{" "}
                    <strong>Level 2 Approval Required</strong> on or off, choose the <strong>Level 2 Approver</strong>, and set
                    after how many days of leave Level 2 approval is needed.
                  </p>
                </div>
              </div>
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">1</span>
                <div>
                  <strong>Employee applies for leave</strong>
                  <p>The request is submitted and enters the approval queue.</p>
                </div>
              </div>
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">2</span>
                <div>
                  <strong>Level 1 — Reporting Manager</strong>
                  <p>The reporting manager reviews the request and approves or rejects it.</p>
                </div>
              </div>
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">3</span>
                <div>
                  <strong>Level 2 — HR Manager</strong>
                  <p>If enabled, the HR manager gives final approval after the reporting manager approves.</p>
                </div>
              </div>
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">4</span>
                <div>
                  <strong>Employee is notified</strong>
                  <p>The leave balance is updated once the request is fully approved.</p>
                </div>
              </div>
            </div>
            <p className="lss-guide-note">Use the Edit button to configure approval levels for each leave type.</p>
          </div>
        )}
      </div>

      <div className="als-table-wrap">
        <table className="als-table">
          <thead>
            <tr>
              <th>Workflow Name</th>
              <th>Level 1 Approver</th>
              <th>Level 2 Approver</th>
              <th>Level 2 Approval Required</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="als-table-empty">
                  <Loader2 size={18} className="als-spin" /> Loading workflows…
                </td>
              </tr>
            ) : paged.length === 0 ? (
              <>
                <tr>
                  <td colSpan={6} className="als-table-empty">
                    No workflows yet. A workflow appears here for each leave type once leave types are created.
                  </td>
                </tr>
              </>
            ) : (
              paged.map((row) => (
                <tr
                  key={row.leaveType.leave_type_id}
                  className={[
                    !row.isActive ? "lss-row-inactive" : "",
                    String(row.leaveType.leave_type_id) === String(highlightId) ? "lss-row-new" : "",
                  ].filter(Boolean).join(" ")}
                >
                  <td className="als-td-name">{row.name}</td>
                  {/* No approvers are shown until the accrual policy is configured. */}
                  <td>{row.policyPending ? <span className="lss-td-dash">—</span> : row.level1}</td>
                  <td>{!row.policyPending && row.level2 ? row.level2 : <span className="lss-td-dash">—</span>}</td>
                  <td>
                    {row.policyPending ? (
                      <span className="lss-td-dash">—</span>
                    ) : (
                      <span className={`lss-badge ${row.level2Required ? "lss-badge--yes" : "lss-badge--no"}`}>
                        {row.level2Required ? "Yes" : "No"}
                      </span>
                    )}
                  </td>
                  <td>
                    {row.isActive && row.policyPending ? (
                      <span className="als-status-pill als-status-pill--draft">Not Configured</span>
                    ) : (
                      <span className={`als-status-pill ${row.isActive ? "als-status-pill--active" : "als-status-pill--inactive"}`}>
                        {row.isActive ? "Active" : "Inactive"}
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="als-action-btns">
                      {row.isActive && row.policyPending ? (
                        <button
                          type="button"
                          className="lss-link-btn"
                          title="Configure the accrual policy first"
                          onClick={() => setSearchParams({ tab: "leave-policies" }, { replace: true })}
                        >
                          Configure accrual policy
                        </button>
                      ) : row.isActive ? (
                        <button
                          type="button"
                          className="als-icon-btn als-icon-btn--edit"
                          title="Edit"
                          onClick={() => onEdit(row.leaveType.leave_type_id)}
                        >
                          <Pencil size={14} />
                        </button>
                      ) : (
                        <span className="lss-td-dash" title="Activate the leave type to edit its workflow">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} total={rows.length} pageSize={PAGE_SIZE} onPage={setPage} />

    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

const LEAVE_TYPE_FORM_VIEWS = new Set(["add", "edit"]);

/** Which required leave steps are actually configured (not merely visited). */
function summarizeLeaveSetup(types, pols) {
  const activeTypes = (Array.isArray(types) ? types : []).filter((t) => t.is_active !== false && !t.is_archived);
  const configured = new Set(
    (Array.isArray(pols) ? pols : [])
      .filter((p) => p.status !== "draft" && p.is_active)
      .map((p) => String(p.leave_type_id))
  );
  const pendingPolicies = activeTypes.filter((t) => !configured.has(String(t.leave_type_id)));
  return { activeTypes, pendingPolicies };
}

function LeaveSetupStepper({ activeIndex, stepDone, onSelect }) {
  return (
    <nav className="lss-stepper" aria-label="Leave setup steps">
      <ol className="lss-stepper-list">
        {TABS.map((tab, idx) => {
          const isActive = idx === activeIndex;
          // A step before the current one is "done" only when its configuration is verified.
          const isPending = idx < activeIndex && stepDone[tab.key] === false;
          const isDone = idx < activeIndex && !isPending;
          const state = isActive ? "active" : isDone ? "done" : "upcoming";
          return (
            <li key={tab.key} className={`lss-step lss-step--${state}${isPending ? " lss-step--pending" : ""}`}>
              <button
                type="button"
                className="lss-step-btn"
                onClick={() => onSelect(tab.key)}
                aria-current={isActive ? "step" : undefined}
                title={isPending ? `${tab.label} — not completed yet` : tab.label}
              >
                <span className="lss-step-dot">
                  {isDone ? <Check size={13} strokeWidth={3} aria-hidden /> : idx + 1}
                </span>
                <span className="lss-step-label">
                  {tab.label}
                  {isDone && <span className="lss-sr-only"> (completed)</span>}
                  {isPending && <span className="lss-sr-only"> (not completed)</span>}
                </span>
              </button>
              {idx < TABS.length - 1 && (
                <span className={`lss-step-line${isDone ? " lss-step-line--done" : ""}`} aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

export default function LeaveSetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { refetch: refetchSetupProgress } = useSetupProgress() || {};
  const { setExtraCrumb } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "leave-cycle";
  const view = searchParams.get("view");
  const editId = searchParams.get("id");
  const { orgId, loading: orgLoading } = useLeaveOrg();
  const [finishing, setFinishing] = useState(false);
  const [stepDone, setStepDone] = useState({});
  const [highlightId, setHighlightId] = useState(null);
  const [highlightPolicyId, setHighlightPolicyId] = useState(null);

  // Full-page forms: Add/Edit Leave Type, Edit Accrual Policy.
  const formView =
    activeTab === "leave-types" && LEAVE_TYPE_FORM_VIEWS.has(view) && (view === "add" || editId)
      ? view
      : activeTab === "leave-policies" && view === "edit" && editId
        ? "edit-policy"
        : activeTab === "approval-workflow" && view === "edit" && editId
          ? "edit-workflow"
          : null;

  const setTab = (key) => setSearchParams({ tab: key }, { replace: true });
  const tabIndex = Math.max(0, TABS.findIndex((t) => t.key === activeTab));
  const hasNextTab = tabIndex >= 0 && tabIndex < TABS.length - 1;

  const refreshStepStatus = useCallback(async () => {
    if (!orgId) return;
    try {
      const [settings, types, pols] = await Promise.all([
        fetchLeaveSettings(orgId).catch(() => null),
        fetchLeaveTypes(orgId),
        fetchLeavePolicies(orgId),
      ]);
      const { activeTypes, pendingPolicies } = summarizeLeaveSetup(types, pols);
      setStepDone({
        // Unknown (undefined) when settings could not be read — shown by position only.
        "leave-cycle": settings ? Boolean(settings.year_cycle) : undefined,
        "leave-types": activeTypes.length > 0,
        "leave-policies": activeTypes.length > 0 && pendingPolicies.length === 0,
      });
    } catch {
      // Keep the last known status; the stepper falls back to step position.
    }
  }, [orgId]);

  useEffect(() => {
    if (!formView) refreshStepStatus();
  }, [refreshStepStatus, activeTab, formView]);

  const handleStepSaved = () => {
    refetchSetupProgress?.();
    refreshStepStatus();
  };

  // Breadcrumb: All Settings > Leave Settings > Leave Types > Add/Edit Leave Type (or Edit Accrual Policy)
  useEffect(() => {
    if (!setExtraCrumb) return undefined;
    const crumbs = {
      add: "Add Leave Type",
      edit: "Edit Leave Type",
      "edit-policy": "Edit Accrual Policy",
      "edit-workflow": "Edit Workflow",
    };
    setExtraCrumb(crumbs[formView] || null);
    return () => setExtraCrumb(null);
  }, [formView, setExtraCrumb]);

  useEffect(() => {
    if (formView) window.scrollTo({ top: 0 });
  }, [formView]);

  const openLeaveTypeForm = (id) => {
    setHighlightId(null);
    setSearchParams(
      id != null ? { tab: "leave-types", view: "edit", id: String(id) } : { tab: "leave-types", view: "add" },
      { state: { fromList: true } }
    );
  };

  const openPolicyForm = (id) => {
    setHighlightPolicyId(null);
    setSearchParams({ tab: "leave-policies", view: "edit", id: String(id) }, { state: { fromList: true } });
  };

  const closeForm = () => {
    // Opened from the list → step back in history; deep link → replace with the list.
    if (location.state?.fromList) navigate(-1);
    else setSearchParams({ tab: activeTab }, { replace: true });
  };
  const closeLeaveTypeForm = closeForm;

  const [highlightWorkflowId, setHighlightWorkflowId] = useState(null);

  const openWorkflowForm = (id) => {
    setHighlightWorkflowId(null);
    setSearchParams({ tab: "approval-workflow", view: "edit", id: String(id) }, { state: { fromList: true } });
  };

  const handleWorkflowSaved = (savedId) => {
    setHighlightWorkflowId(savedId);
    handleStepSaved();
    closeForm();
  };

  const handlePolicySaved = (savedId) => {
    setHighlightPolicyId(savedId);
    handleStepSaved();
    closeForm();
  };

  const handleLeaveTypeSaved = (savedId) => {
    setHighlightId(savedId);
    handleStepSaved();
    closeLeaveTypeForm();
  };

  /**
   * Required leave configuration, checked before moving past Leave Types /
   * Accrual Policy and before Submit returns to Settings. Returns the tab key
   * the user still has to complete, or null when everything is configured.
   */
  const findIncompleteStep = async (upToTab) => {
    const order = TABS.map((t) => t.key);
    const reach = order.indexOf(upToTab);
    const [types, pols] = await Promise.all([fetchLeaveTypes(orgId), fetchLeavePolicies(orgId)]);
    const { activeTypes, pendingPolicies } = summarizeLeaveSetup(types, pols);
    if (reach >= order.indexOf("leave-types") && activeTypes.length === 0) {
      return { tab: "leave-types", message: "Create at least one leave type before continuing." };
    }
    if (reach >= order.indexOf("leave-policies") && pendingPolicies.length) {
      const names = pendingPolicies.map((t) => t.code || t.name).join(", ");
      return { tab: "leave-policies", message: `Configure the accrual policy for: ${names}.` };
    }
    return null;
  };

  const [checking, setChecking] = useState(false);

  const goNextTab = async () => {
    if (!hasNextTab) return;
    // Leaving Leave Types / Accrual Policy requires that step to be complete.
    if (activeTab === "leave-types" || activeTab === "leave-policies") {
      setChecking(true);
      try {
        const gap = await findIncompleteStep(activeTab);
        if (gap) {
          toast.error(gap.message);
          if (gap.tab !== activeTab) setTab(gap.tab);
          return;
        }
      } catch (e) {
        toast.error(fmtErr(e, "Could not verify leave setup."));
        return;
      } finally {
        setChecking(false);
      }
    }
    setTab(TABS[tabIndex + 1].key);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrevTab = () => {
    if (tabIndex <= 0) return;
    setTab(TABS[tabIndex - 1].key);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const finishLeaveSetup = async () => {
    try {
      setFinishing(true);
      const gap = await findIncompleteStep("approval-workflow");
      if (gap) {
        toast.error(gap.message);
        setTab(gap.tab);
        return;
      }
      await markLeaveSetupConfigured();
      await refetchSetupProgress?.();
      toast.success("Leave setup complete. Returning to All Settings…");
      navigateToSettingsHome(navigate, { replace: true });
    } catch (e) {
      toast.error(fmtErr(e, "Could not finish leave setup."));
    } finally {
      setFinishing(false);
    }
  };

  if (orgLoading) {
    return (
      <div className="als-root lss-page">
        <div className="als-loading">
          <Loader2 size={22} className="als-spin" />
          Loading…
        </div>
      </div>
    );
  }

  if (formView === "edit-workflow") {
    return (
      <div className="als-root lss-page">
        <WorkflowFormPage
          key={`workflow-${editId}`}
          orgId={orgId}
          leaveTypeId={editId}
          onClose={closeForm}
          onSaved={handleWorkflowSaved}
        />
      </div>
    );
  }

  if (formView === "edit-policy") {
    return (
      <div className="als-root lss-page">
        <AccrualPolicyFormPage
          key={`policy-${editId}`}
          orgId={orgId}
          policyId={editId}
          onClose={closeForm}
          onSaved={handlePolicySaved}
        />
      </div>
    );
  }

  if (formView) {
    return (
      <div className="als-root lss-page">
        <LeaveTypeFormPage
          key={`${formView}-${editId || "new"}`}
          orgId={orgId}
          leaveTypeId={formView === "edit" ? editId : null}
          onClose={closeLeaveTypeForm}
          onSaved={handleLeaveTypeSaved}
        />
      </div>
    );
  }

  return (
    <div className="als-root lss-page">
      <LeaveSetupStepper activeIndex={tabIndex} stepDone={stepDone} onSelect={setTab} />

      <div className="als-content">
        {activeTab === "leave-cycle" && (
          <LeaveCycleTab orgId={orgId} onSaved={handleStepSaved} />
        )}
        {activeTab === "leave-types" && (
          <LeaveTypesTab
            orgId={orgId}
            onAdd={() => openLeaveTypeForm(null)}
            onEdit={(id) => openLeaveTypeForm(id)}
            highlightId={highlightId}
          />
        )}
        {activeTab === "leave-policies" && (
          <LeavePoliciesTab
            orgId={orgId}
            onEdit={(id) => openPolicyForm(id)}
            highlightId={highlightPolicyId}
          />
        )}
        {activeTab === "approval-workflow" && (
          <LeaveApprovalWorkflowTab
            orgId={orgId}
            onEdit={(id) => openWorkflowForm(id)}
            highlightId={highlightWorkflowId}
          />
        )}
      </div>

      <div className="als-setup-nav">
        <button
          type="button"
          className="lss-btn-secondary"
          onClick={goPrevTab}
          disabled={tabIndex <= 0 || finishing || checking}
        >
          <ChevronLeft size={15} /> Back
        </button>
        {hasNextTab ? (
          <button type="button" className="lss-btn-primary" onClick={goNextTab} disabled={finishing || checking}>
            {checking ? "Checking…" : "Next"} <ArrowRight size={15} />
          </button>
        ) : (
          <button
            type="button"
            className="lss-btn-primary"
            onClick={finishLeaveSetup}
            disabled={finishing}
          >
            {finishing ? "Submitting…" : "Submit"}
          </button>
        )}
      </div>
    </div>
  );
}
